import { useContext } from 'react';
import axios from 'axios';
import { getFirstAndLastDayOfMonth, getTotalDaysInMonth } from './RhythmUtils';
import {
      fetchGoogleFitAllDataForMonth,
      fetchGoogleFitActivity,
      fetchWalletData as generateWalletMockData
} from './RhythmHelpers';
import Logger from '../../../utils/Logger';
import { AuthUserContext } from '../../session/context';
import config from '../../../utils/EnvironmentSwitcher';
import { buildFhirHeaders } from '../../../utils/fhirAuth';

const FHIR_PROXY_BASE = `${String(config.apiUrl || '').replace(/\/+$/, '')}/api/fhir`;
// fhirSystemBase is canonical — FHIR identifier system URIs are permanent labels baked into
// stored resources and do NOT change between dev/prod environments.
const FHIR_ID_SYSTEM = `${String(config.fhirSystemBase || '').replace(/\/+$/, '')}/fhir/ids`;

const getFhirHeaders = async (forceRefresh = false) => {
      return buildFhirHeaders({ 'Content-Type': 'application/fhir+json' }, forceRefresh);
};

const axiosGetWithRetry = async (url) => {
      const headers = await getFhirHeaders(false);
      if (!headers.Authorization) {
            throw new Error('Missing Firebase ID token for /api/fhir proxy');
      }
      try {
            return await axios.get(url, { headers });
      } catch (error) {
            if (error?.response?.status === 401) {
                  const refreshedHeaders = await getFhirHeaders(true);
                  if (!refreshedHeaders.Authorization) {
                        throw new Error('Unable to refresh Firebase ID token for /api/fhir proxy');
                  }
                  return axios.get(url, { headers: refreshedHeaders });
            }
            throw error;
      }
};

export const fetchDebitsFromFHIR = async (firstDay, lastDay, authUser) => {
      try {
            const fhirEndpoint = FHIR_PROXY_BASE;
            const headers = await getFhirHeaders(false);
            if (!headers.Authorization) {
                  console.error('No authentication token found.');
                  return [];
            }

            const queryUrl = `${fhirEndpoint}/ChargeItem?entered-date=ge${firstDay.toISOString()}&entered-date=le${lastDay.toISOString()}&identifier=${encodeURIComponent(`${FHIR_ID_SYSTEM}|${authUser.uid}`)}`;

            const response = await axiosGetWithRetry(queryUrl);

            const chargeItems = response.data.entry || [];
            const debits = chargeItems.map((entry) => {
                  const item = entry.resource;
                  return {
                        date: item.enteredDate || item.servicedDate?.start,
                        amount: item.quantity.value,
                  };
            });

            return debits;
      } catch (error) {
            console.error('Error fetching debits from FHIR:', error);
            return [];
      }
};

export const fetchWalletData = async (setters = {}) => {
      const { 
            setCRData = () => {}, 
            setStepsData = () => {}, 
            setCaloriesData = () => {}, 
            setCaloriesTodayData = () => {}, 
            setSleepData = () => {}, 
            setWeightData = () => {}, 
            setNutritionData = () => {} 
      } = setters;
      const { firstDay, lastDay } = getFirstAndLastDayOfMonth();
      try {
            const mockData = await generateWalletMockData(firstDay, lastDay);

            if (!mockData) {
                  throw new Error('No mock data generated');
            }

            // Store the mock data
            localStorage.setItem('walletFitStepData', JSON.stringify(mockData.steps));
            localStorage.setItem('walletFitActivityData', JSON.stringify(mockData.activity));
            localStorage.setItem('walletFitCalorieData', JSON.stringify(mockData.calories));
            localStorage.setItem('walletFitSleepData', JSON.stringify(mockData.sleep));
            localStorage.setItem('walletFitWeightData', JSON.stringify(mockData.weight));
            localStorage.setItem('walletFitNutritionData', JSON.stringify(mockData.nutrition));

            const labels = Array.from({ length: mockData.steps.length }, (_, i) => (i + 1).toString());

            setCRData({
                  labels,
                  datasets: [
                        {
                              label: 'Steps',
                              data: mockData.steps,
                              backgroundColor: 'rgba(0, 123, 255, 0.2)',
                              borderColor: '#007bff',
                        },
                        {
                              label: 'Calories',
                              data: mockData.calories,
                              backgroundColor: 'rgba(255, 193, 7, 0.2)',
                              borderColor: '#ffc107',
                        },
                        {
                              label: 'Weight',
                              backgroundColor: 'rgba(128, 0, 128, 0.2)',
                              borderColor: '#800080',
                              data: mockData.weight,
                        },
                        {
                              label: 'Sleep',
                              backgroundColor: 'rgba(255, 0, 0, 0.2)',
                              borderColor: '#FF0000',
                              data: mockData.sleep,
                        },
                        {
                              label: 'Nutrition',
                              backgroundColor: 'rgba(0, 128, 0, 0.2)',
                              borderColor: '#008000',
                              data: mockData.nutrition,
                        }
                  ],
            });

            // Update current values with null checks
            setStepsData(mockData.steps?.[mockData.steps.length - 1] ?? 0);
            setCaloriesData(mockData.calories?.[mockData.calories.length - 1] ?? 0);
            setCaloriesTodayData(mockData.calories?.[new Date().getDate() - 1] ?? 0);
            setSleepData(mockData.sleep?.[mockData.sleep.length - 1] ?? 0);
            setWeightData(mockData.weight?.[mockData.weight.length - 1] ?? 0);
            setNutritionData(mockData.nutrition?.[mockData.nutrition.length - 1] ?? 0);

      } catch (error) {
            console.error('Error fetching wallet data:', error);
            // Set default values in case of error
            setCRData({
                  labels: [],
                  datasets: []
            });
            setStepsData(0);
            setCaloriesData(0);
            setCaloriesTodayData(0);
            setSleepData(0);
            setWeightData(0);
            setNutritionData(0);
      }
};

export const fetchObservationsFromFHIR = async (firstDay, lastDay, authUser) => {
      try {
            const fhirEndpoint = FHIR_PROXY_BASE;
            const headers = await getFhirHeaders(false);
            if (!headers.Authorization) {
                  console.error('No authentication token found.');
                  return [];
            }

            let allObservations = [];
            let nextUrl = `${fhirEndpoint}/Observation?date=ge${firstDay.toISOString()}&date=le${lastDay.toISOString()}&identifier=${encodeURIComponent(`${FHIR_ID_SYSTEM}|${authUser.uid}`)}&_count=100`;
            let pageCount = 0;
            const visitedUrls = new Set();
            const MAX_PAGES = 25;

            // Keep fetching while there's a next page
            while (nextUrl) {
                  if (visitedUrls.has(nextUrl) || pageCount >= MAX_PAGES) {
                        break;
                  }
                  visitedUrls.add(nextUrl);
                  pageCount += 1;

                  //Logger.debug('Fetching FHIR observations from:', nextUrl);
                  
                  const response = await axiosGetWithRetry(nextUrl);

                  // Add current page's observations to our collection
                  if (response.data.entry) {
                        allObservations = [...allObservations, ...response.data.entry];
                  }

                  // Check for next page
                  nextUrl = null; // Reset nextUrl
                  if (response.data.link) {
                        const nextLink = response.data.link.find(link => link.relation === 'next');
                        if (nextLink) {
                              // Ensure HTTPS is used
                              nextUrl = nextLink.url.replace('http://', 'https://');
                              Logger.debug('Found next page:', nextUrl);
                        }
                  }
            }

           // Logger.debug(`Total observations fetched: ${allObservations.length}`);

            // Group observations by type and date
            const observationsByTypeAndDate = {};
            allObservations.forEach(entry => {
                  const item = entry.resource;
                  const type = item.code?.text ||
                        item.code?.coding?.[0]?.display ||
                        'Unknown';

                  if (!type) return;

                  const date = new Date(item.effectiveDateTime || item.meta?.lastUpdated);
                  const dayIndex = date.getDate() - 1; // Convert to 0-based index
                  const value = item.valueQuantity?.value || 0;

                  if (!observationsByTypeAndDate[type]) {
                        observationsByTypeAndDate[type] = new Array(lastDay.getDate()).fill(0);
                  }

                  // Sum values for the same type and date
                  observationsByTypeAndDate[type][dayIndex] += value;
            });

            // Convert grouped data to final format
            const result = Object.entries(observationsByTypeAndDate).map(([type, data]) => ({
                  type,
                  data,
                  source: 'fhir'
            }));

           // Logger.debug('Final processed observations:', result);
            return result;

      } catch (error) {
            Logger.error('Error fetching FHIR observations:', error);
            return [];
      }
};

